import React, { useState, useEffect } from "react";
import { Quote, Star, ChevronLeft, ChevronRight } from "lucide-react";
import { testimonial } from "../../images";
import { motion } from "framer-motion";


const testimonials = [
  {
    quote: "This platform has transformed my teaching approach",
    author: "John Doe",
    role: "High School Teacher",
    testimonial:
      "The seamless integration of features like video conferencing and assignment management has streamlined my workflow and allowed me to focus more on teaching",
    rating: 5,
  },
  {
    quote: "I've been able to increase student engagement and improve",
    author: "Jane Smith",
    role: "Middle School Educator",
    testimonial:
      "As a teacher, I've found this platform to be an invaluable tool in my toolkit. It's helped me create a more collaborative and interactive learning environment for my students",
    rating: 5,
  },
  {
    quote: "The most intuitive education platform I've ever used",
    author: "Robert Johnson",
    role: "University Professor",
    testimonial:
      "After trying numerous educational tools, this is the only one that truly understands what educators need. The interface is intuitive and the features are comprehensive.",
    rating: 5,
  },
  {
    quote: "My students love the interactive elements",
    author: "Sarah Williams",
    role: "Elementary School Teacher",
    testimonial:
      "The platform's engagement tools have made a remarkable difference in my classroom. Students who were previously reluctant to participate are now actively involved in discussions.",
    rating: 4,
  },
];

const Testimonials: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      handleNext();
    }, 8000);

    return () => clearInterval(interval);
  }, [activeIndex]);

  const handlePrev = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setActiveIndex((prev) => (prev === 0 ? testimonials.length - 1 : prev - 1));
    setTimeout(() => setIsAnimating(false), 500);
  };

  const handleNext = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setActiveIndex((prev) => (prev === testimonials.length - 1 ? 0 : prev + 1));
    setTimeout(() => setIsAnimating(false), 500);
  };

  const goToTestimonial = (index: number) => {
    if (isAnimating) return;
    setIsAnimating(true);
    setActiveIndex(index);
    setTimeout(() => setIsAnimating(false), 500);
  };

  // Render stars based on rating
  const renderStars = (rating: number) => {
    return Array(5)
      .fill(0)
      .map((_, i) => (
        <Star
          key={i}
          size={16}
          className={`${
            i < rating ? "text-yellow-400 fill-yellow-400" : "text-gray-300"
          }`}
        />
      ));
  };

  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-indigo-50 to-white py-24">
      {/* Background decorative elements */}
      <div className="absolute top-0 left-0 w-full h-64 bg-indigo-100 transform -skew-y-3"></div>
      <div className="absolute -bottom-10 right-0 w-96 h-96 bg-indigo-100 rounded-full opacity-20"></div>
      <div className="absolute top-40 right-20 w-48 h-48 bg-indigo-200 rounded-full opacity-10"></div>
      <div className="absolute bottom-20 left-10 w-32 h-32 bg-indigo-300 rounded-full opacity-10"></div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header Section with animated underline */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center justify-center bg-indigo-100 text-indigo-600 px-4 py-2 rounded-full text-sm font-medium mb-4">
            User Experiences
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 relative inline-block">
            TESTIMONIALS
            <span className="absolute bottom-0 left-0 w-full h-1 bg-indigo-600 rounded-full"></span>
          </h2>
          <p className="text-lg text-gray-600 mt-4">
            Hear from our satisfied users who've transformed their teaching
          </p>
        </motion.div>

        <div className="max-w-6xl mx-auto">
          <div className="relative flex flex-col lg:flex-row items-center">
            {/* Featured Image with overlay */}
            <div className="relative w-full lg:w-2/5 mb-12 lg:mb-0">
              <div className="relative rounded-lg overflow-hidden shadow-2xl">
                <div className="absolute inset-0 bg-indigo-900 opacity-30 z-10"></div>
                <div className="absolute inset-0 z-20 flex items-center justify-center">
                  <Quote size={80} className="text-white opacity-50" />
                </div>
                <div className="w-full pb-[75%] relative">
                  <img
                    src={testimonial}
                    alt="Students and teachers collaborating"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* Floating stats box */}
              <div className="absolute bottom-0 lg:bottom-8 right-0 lg:-right-8 bg-white shadow-xl rounded-lg p-6 max-w-xs transform translate-y-1/2 lg:translate-y-0">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-bold text-gray-800">
                    Trusted by Educators
                  </h4>
                  <div className="bg-indigo-600 text-white text-xs font-medium px-3 py-1 rounded-full">
                    Verified
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-3xl font-bold text-indigo-600">98%</p>
                    <p className="text-sm text-gray-500">Satisfaction Rate</p>
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-indigo-600">5000+</p>
                    <p className="text-sm text-gray-500">Active Users</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Testimonial Carousel */}
            <div className="w-full lg:w-3/5 lg:pl-16">
              <div
                className="relative overflow-hidden transition-all duration-500 ease-in-out"
                style={{ minHeight: "400px" }}>
                {testimonials.map((testimonial, index) => (
                  <div
                    key={index}
                    className={`absolute inset-0 transition-all duration-500 ease-in-out flex flex-col ${
                      index === activeIndex
                        ? "opacity-100 translate-x-0 z-10"
                        : index < activeIndex
                        ? "opacity-0 -translate-x-full z-0"
                        : "opacity-0 translate-x-full z-0"
                    }`}>
                    {/* Quote icon */}
                    <div className="mb-6">
                      <Quote size={40} className="text-indigo-200" />
                    </div>

                    {/* Main quote */}
                    <h3 className="text-2xl md:text-3xl font-medium text-gray-800 mb-4">
                      "{testimonial.quote}"
                    </h3>

                    {/* Full testimonial */}
                    <p className="text-lg text-gray-600 mb-4 leading-relaxed">
                      {testimonial.testimonial}
                    </p>

                    {/* Rating */}
                    <div className="flex items-center mb-4">
                      {renderStars(testimonial.rating)}
                    </div>

                    {/* Author info */}
                    <div className="mt-auto flex items-center">
                      <div className="bg-indigo-100 text-indigo-600 font-bold w-12 h-12 rounded-full flex items-center justify-center text-xl">
                        {testimonial.author.charAt(0)}
                      </div>
                      <div className="ml-4">
                        <h4 className="font-bold text-gray-800">
                          {testimonial.author}
                        </h4>
                        <p className="text-gray-500">{testimonial.role}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Navigation controls */}
              <div className="flex items-center justify-between mt-10">
                <div className="flex space-x-2">
                  {testimonials.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => goToTestimonial(index)}
                      className={`w-3 h-3 rounded-full transition-all duration-300 ${
                        index === activeIndex
                          ? "bg-indigo-600 w-8"
                          : "bg-gray-300"
                      }`}
                      aria-label={`Go to testimonial ${index + 1}`}
                    />
                  ))}
                </div>

                <div className="flex space-x-4">
                  <button
                    onClick={handlePrev}
                    className="p-2 rounded-full border border-gray-300 hover:border-indigo-600 hover:text-indigo-600 transition-all duration-300"
                    aria-label="Previous testimonial">
                    <ChevronLeft size={20} />
                  </button>
                  <button
                    onClick={handleNext}
                    className="p-2 rounded-full border border-gray-300 hover:border-indigo-600 hover:text-indigo-600 transition-all duration-300"
                    aria-label="Next testimonial">
                    <ChevronRight size={20} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
